import React, { useState } from 'react';
import {
  Card,
  Table,
  Switch,
  Button,
  Typography,
  Space,
  Tag,
  Tooltip,
  Row,
  Col,
  Alert,
  Popconfirm,
} from 'antd';
import { staticApi } from '../utils/staticApi';
import {
  LockOutlined,
  UnlockOutlined,
  ReloadOutlined,
  SaveOutlined,
  InfoCircleOutlined,
  TeamOutlined,
  UserOutlined,
  CrownOutlined,
  // Feature icons
  DashboardOutlined,
  BankOutlined,
  CarOutlined,
  SafetyCertificateOutlined,
  WarningOutlined,
  HomeOutlined,
  CreditCardOutlined,
  DollarOutlined,
  ArrowDownOutlined,
  FilePdfOutlined,
  BarChartOutlined,
  ToolOutlined,
  CalendarOutlined,
  ProjectOutlined,
  FileTextOutlined,
  AuditOutlined,
  MailOutlined,
  CheckCircleOutlined,
  FileSearchOutlined,
  InboxOutlined,
  ReadOutlined,
} from '@ant-design/icons';
import { useAuthStore, DEFAULT_FEATURE_SETTINGS, type FeatureVisibility } from '../store/authStore';

const { Title, Text } = Typography;

interface FeatureRow {
  key: string;
  label: string;
  category: string;
  basePermission: string;
  icon: React.ReactNode;
}

const ALL_FEATURES: FeatureRow[] = [
  // Overview
  { key: 'overview',            label: 'Dashboard Overview',    category: 'Overview',     basePermission: 'all',          icon: <DashboardOutlined /> },
  // Property
  { key: 'condos',              label: 'Condominiums',          category: 'Property',     basePermission: 'super_admin',  icon: <BankOutlined /> },
  { key: 'parking',             label: 'Parking Inventory',     category: 'Property',     basePermission: 'admin',        icon: <CarOutlined /> },
  // Residents
  { key: 'owners',              label: 'Resident Management',   category: 'Residents',    basePermission: 'admin',        icon: <TeamOutlined /> },
  { key: 'violations',          label: 'Violations',            category: 'Residents',    basePermission: 'admin',        icon: <WarningOutlined /> },
  { key: 'board',               label: 'Board Management',      category: 'Residents',    basePermission: 'admin',        icon: <SafetyCertificateOutlined /> },
  { key: 'portal',              label: 'Resident Portal',       category: 'Residents',    basePermission: 'all',          icon: <HomeOutlined /> },
  // Finances
  { key: 'fees',                label: 'Fee Management',        category: 'Finances',     basePermission: 'admin',        icon: <DollarOutlined /> },
  { key: 'expenses',            label: 'Expense Tracking',      category: 'Finances',     basePermission: 'admin',        icon: <ArrowDownOutlined /> },
  { key: 'statements',          label: 'Financial Statements',  category: 'Finances',     basePermission: 'admin',        icon: <FilePdfOutlined /> },
  { key: 'payments',            label: 'Payment Processing',    category: 'Finances',     basePermission: 'all',          icon: <CreditCardOutlined /> },
  { key: 'reports',             label: 'Analytics & Reports',   category: 'Finances',     basePermission: 'admin',        icon: <BarChartOutlined /> },
  // Operations
  { key: 'maintenance',         label: 'Maintenance Requests',  category: 'Operations',   basePermission: 'admin',        icon: <ToolOutlined /> },
  { key: 'amenities',           label: 'Amenity Booking',       category: 'Operations',   basePermission: 'all',          icon: <CalendarOutlined /> },
  { key: 'projects',            label: 'Projects',              category: 'Operations',   basePermission: 'admin',        icon: <ProjectOutlined /> },
  { key: 'documents',           label: 'Document Centre',       category: 'Operations',   basePermission: 'all',          icon: <FileTextOutlined /> },
  { key: 'status-certificates', label: 'Status Certificates',   category: 'Operations',   basePermission: 'admin',        icon: <AuditOutlined /> },
  { key: 'email-center',        label: 'Email Centre',          category: 'Operations',   basePermission: 'admin',        icon: <MailOutlined /> },
  { key: 'work-orders',         label: 'Work Orders',           category: 'Operations',   basePermission: 'admin',        icon: <CheckCircleOutlined /> },
  { key: 'inspections',         label: 'Inspections',           category: 'Operations',   basePermission: 'admin',        icon: <FileSearchOutlined /> },
  { key: 'packages',            label: 'Package Tracking',      category: 'Operations',   basePermission: 'admin',        icon: <InboxOutlined /> },
  { key: 'amenity-management',  label: 'Amenity Management',    category: 'Operations',   basePermission: 'admin',        icon: <HomeOutlined /> },
  { key: 'tutorial',            label: 'Learning Centre',       category: 'Overview',     basePermission: 'all',          icon: <ReadOutlined /> },
];

const CATEGORY_COLORS: Record<string, string> = {
  Overview:   '#722ed1',
  Property:   '#1890ff',
  Residents:  '#13c2c2',
  Finances:   '#52c41a',
  Operations: '#fa8c16',
  System:     '#f5222d',
};

export const FeaturePermissions: React.FC = () => {
  const { featureSettings, setFeatureSettings, resetFeatureSettings } = useAuthStore();
  const { user } = useAuthStore();

  // Local copy for editing (commit on Save)
  const [localSettings, setLocalSettings] = useState(() => ({ ...featureSettings }));
  const [isDirty, setIsDirty] = useState(false);

  const isSuperAdmin = user?.role === 'super_admin_condo';

  const toggleLocal = (key: string, role: keyof FeatureVisibility, value: boolean) => {
    setLocalSettings(prev => ({
      ...prev,
      [key]: {
        ...(prev[key] || DEFAULT_FEATURE_SETTINGS[key] || { adminVisible: true, ownerVisible: false, boardMemberVisible: false }),
        [role]: value,
      }
    }));
    setIsDirty(true);
  };

  const handleSave = () => {
    setFeatureSettings(localSettings);
    setIsDirty(false);
    staticApi.message.success({ content: 'Feature permissions saved successfully', icon: '✅' });
  };

  const handleReset = () => {
    setLocalSettings({ ...DEFAULT_FEATURE_SETTINGS });
    resetFeatureSettings();
    setIsDirty(false);
    staticApi.message.info('Feature permissions reset to defaults');
  };

  const handleEnableAll = (role: keyof FeatureVisibility) => {
    const updated = { ...localSettings };
    ALL_FEATURES.forEach(f => {
      // Never touch super_admin-only features
      if (f.basePermission === 'super_admin') return;
      updated[f.key] = {
        ...(updated[f.key] || DEFAULT_FEATURE_SETTINGS[f.key] || { adminVisible: true, ownerVisible: false, boardMemberVisible: false }),
        [role]: true,
      };
    });
    setLocalSettings(updated);
    setIsDirty(true);
    staticApi.message.success(`Enabled all features for ${roleLabel(role)}`);
  };

  const handleDisableAll = (role: keyof FeatureVisibility) => {
    const updated = { ...localSettings };
    ALL_FEATURES.forEach(f => {
      if (f.basePermission === 'super_admin') return;
      updated[f.key] = {
        ...(updated[f.key] || DEFAULT_FEATURE_SETTINGS[f.key] || { adminVisible: true, ownerVisible: false, boardMemberVisible: false }),
        [role]: false,
      };
    });
    setLocalSettings(updated);
    setIsDirty(true);
    staticApi.message.info(`Disabled all features for ${roleLabel(role)}`);
  };

  const roleLabel = (role: keyof FeatureVisibility) => {
    if (role === 'adminVisible') return 'Admin';
    if (role === 'ownerVisible') return 'Owner';
    return 'Board Member';
  };

  // Group features by category
  const categories = Array.from(new Set(ALL_FEATURES.map(f => f.category)));

  const columns = [
    {
      title: 'Feature',
      dataIndex: 'label',
      key: 'label',
      width: 260,
      render: (label: string, row: FeatureRow) => (
        <Space>
          <div style={{
            width: 32, height: 32,
            borderRadius: 8,
            background: `${CATEGORY_COLORS[row.category]}15`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: CATEGORY_COLORS[row.category],
            fontSize: 15,
            flexShrink: 0,
          }}>
            {row.icon}
          </div>
          <div>
            <Text strong style={{ fontSize: 13, display: 'block' }}>{label}</Text>
            <Tag
              color={CATEGORY_COLORS[row.category]}
              style={{ fontSize: 10, borderRadius: 4, marginTop: 2 }}
            >
              {row.category}
            </Tag>
          </div>
        </Space>
      ),
    },
    {
      title: (
        <Space>
          <CrownOutlined style={{ color: '#f59e0b' }} />
          <Text style={{ color: '#1a202c', fontWeight: 600 }}>Super Admin</Text>
        </Space>
      ),
      key: 'superAdmin',
      width: 130,
      align: 'center' as const,
      render: (_: any, row: FeatureRow) => (
        <Tooltip title="Super Admin always has full access">
          <Tag color="gold" style={{ borderRadius: 6, fontWeight: 600 }}>
            Always On
          </Tag>
        </Tooltip>
      ),
    },
    {
      title: (
        <Space direction="vertical" size={2} align="center" style={{ width: '100%' }}>
          <Space>
            <TeamOutlined style={{ color: '#1890ff' }} />
            <Text style={{ color: '#1a202c', fontWeight: 600 }}>Admin</Text>
          </Space>
          <Space size={4}>
            <Button size="small" type="link" style={{ padding: 0, fontSize: 11 }} onClick={() => handleEnableAll('adminVisible')}>All On</Button>
            <Text style={{ color: '#d9d9d9' }}>|</Text>
            <Button size="small" type="link" style={{ padding: 0, fontSize: 11 }} onClick={() => handleDisableAll('adminVisible')}>All Off</Button>
          </Space>
        </Space>
      ),
      key: 'admin',
      width: 140,
      align: 'center' as const,
      render: (_: any, row: FeatureRow) => {
        if (row.basePermission === 'super_admin') {
          return (
            <Tooltip title="Super Admin only feature">
              <LockOutlined style={{ color: '#d9d9d9', fontSize: 18 }} />
            </Tooltip>
          );
        }
        const setting = localSettings[row.key] ?? DEFAULT_FEATURE_SETTINGS[row.key];
        return (
          <Switch
            checked={setting?.adminVisible ?? true}
            onChange={val => toggleLocal(row.key, 'adminVisible', val)}
            checkedChildren={<UnlockOutlined />}
            unCheckedChildren={<LockOutlined />}
            style={{ minWidth: 52 }}
          />
        );
      },
    },
    {
      title: (
        <Space direction="vertical" size={2} align="center" style={{ width: '100%' }}>
          <Space>
            <UserOutlined style={{ color: '#13c2c2' }} />
            <Text style={{ color: '#1a202c', fontWeight: 600 }}>Owner</Text>
          </Space>
          <Space size={4}>
            <Button size="small" type="link" style={{ padding: 0, fontSize: 11 }} onClick={() => handleEnableAll('ownerVisible')}>All On</Button>
            <Text style={{ color: '#d9d9d9' }}>|</Text>
            <Button size="small" type="link" style={{ padding: 0, fontSize: 11 }} onClick={() => handleDisableAll('ownerVisible')}>All Off</Button>
          </Space>
        </Space>
      ),
      key: 'owner',
      width: 140,
      align: 'center' as const,
      render: (_: any, row: FeatureRow) => {
        if (row.basePermission === 'super_admin') {
          return <LockOutlined style={{ color: '#d9d9d9', fontSize: 18 }} />;
        }
        const setting = localSettings[row.key] ?? DEFAULT_FEATURE_SETTINGS[row.key];
        return (
          <Switch
            checked={setting?.ownerVisible ?? false}
            onChange={val => toggleLocal(row.key, 'ownerVisible', val)}
            checkedChildren={<UnlockOutlined />}
            unCheckedChildren={<LockOutlined />}
            style={{ minWidth: 52 }}
          />
        );
      },
    },
    {
      title: (
        <Space direction="vertical" size={2} align="center" style={{ width: '100%' }}>
          <Space>
            <TeamOutlined style={{ color: '#52c41a' }} />
            <Text style={{ color: '#1a202c', fontWeight: 600 }}>Board Member</Text>
          </Space>
          <Space size={4}>
            <Button size="small" type="link" style={{ padding: 0, fontSize: 11 }} onClick={() => handleEnableAll('boardMemberVisible')}>All On</Button>
            <Text style={{ color: '#d9d9d9' }}>|</Text>
            <Button size="small" type="link" style={{ padding: 0, fontSize: 11 }} onClick={() => handleDisableAll('boardMemberVisible')}>All Off</Button>
          </Space>
        </Space>
      ),
      key: 'boardMember',
      width: 160,
      align: 'center' as const,
      render: (_: any, row: FeatureRow) => {
        if (row.basePermission === 'super_admin') {
          return <LockOutlined style={{ color: '#d9d9d9', fontSize: 18 }} />;
        }
        const setting = localSettings[row.key] ?? DEFAULT_FEATURE_SETTINGS[row.key];
        return (
          <Switch
            checked={setting?.boardMemberVisible ?? false}
            onChange={val => toggleLocal(row.key, 'boardMemberVisible', val)}
            checkedChildren={<UnlockOutlined />}
            unCheckedChildren={<LockOutlined />}
            style={{ minWidth: 52 }}
          />
        );
      },
    },
  ];

  // Count enabled per role
  const countEnabled = (role: keyof FeatureVisibility) =>
    ALL_FEATURES.filter(f => {
      if (f.basePermission === 'super_admin') return false;
      const s = localSettings[f.key] ?? DEFAULT_FEATURE_SETTINGS[f.key];
      return s?.[role] ?? false;
    }).length;

  const totalControllable = ALL_FEATURES.filter(f => f.basePermission !== 'super_admin').length;

  return (
    <div style={{ padding: '0 0 24px' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Space align="start">
            <div style={{
              width: 48, height: 48,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: 12,
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <LockOutlined style={{ color: '#fff', fontSize: 22 }} />
            </div>
            <div>
              <Title level={4} style={{ margin: 0, color: '#1a202c' }}>Feature Permissions</Title>
              <Text type="secondary">Control which features each role can access</Text>
            </div>
          </Space>
        </Col>
        <Col>
          <Space>
            {isDirty && (
              <Tag color="warning" style={{ padding: '4px 12px', borderRadius: 6, fontWeight: 500 }}>
                Unsaved changes
              </Tag>
            )}
            <Popconfirm
              title="Reset to defaults?"
              description="This will restore all feature permissions to their original settings."
              onConfirm={handleReset}
              okText="Reset"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Button icon={<ReloadOutlined />} style={{ borderRadius: 8 }}>
                Reset Defaults
              </Button>
            </Popconfirm>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              disabled={!isDirty}
              style={{ borderRadius: 8 }}
            >
              Save Changes
            </Button>
          </Space>
        </Col>
      </Row>

      <Alert
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
        message="How feature permissions work"
        description="Super Admins always have access to all features. Use the toggles below to control which features Admin, Owner, and Board Member roles can see in their navigation menu. Changes take effect immediately after saving."
        style={{ marginBottom: 24, borderRadius: 8 }}
      />

      {/* Summary Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card
            size="small"
            style={{ borderRadius: 10, border: '1px solid #e6f7ff', background: '#e6f7ff' }}
          >
            <Space>
              <TeamOutlined style={{ color: '#1890ff', fontSize: 20 }} />
              <div>
                <Text style={{ color: '#4a5568', fontSize: 12 }}>Admin Access</Text>
                <div>
                  <Text strong style={{ fontSize: 20, color: '#1890ff' }}>{countEnabled('adminVisible')}</Text>
                  <Text type="secondary" style={{ fontSize: 13 }}> / {totalControllable} features</Text>
                </div>
              </div>
            </Space>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card
            size="small"
            style={{ borderRadius: 10, border: '1px solid #e6fffb', background: '#e6fffb' }}
          >
            <Space>
              <UserOutlined style={{ color: '#13c2c2', fontSize: 20 }} />
              <div>
                <Text style={{ color: '#4a5568', fontSize: 12 }}>Owner Access</Text>
                <div>
                  <Text strong style={{ fontSize: 20, color: '#13c2c2' }}>{countEnabled('ownerVisible')}</Text>
                  <Text type="secondary" style={{ fontSize: 13 }}> / {totalControllable} features</Text>
                </div>
              </div>
            </Space>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card
            size="small"
            style={{ borderRadius: 10, border: '1px solid #f6ffed', background: '#f6ffed' }}
          >
            <Space>
              <TeamOutlined style={{ color: '#52c41a', fontSize: 20 }} />
              <div>
                <Text style={{ color: '#4a5568', fontSize: 12 }}>Board Member Access</Text>
                <div>
                  <Text strong style={{ fontSize: 20, color: '#52c41a' }}>{countEnabled('boardMemberVisible')}</Text>
                  <Text type="secondary" style={{ fontSize: 13 }}> / {totalControllable} features</Text>
                </div>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Features Table grouped by category */}
      {categories.map(category => {
        const rows = ALL_FEATURES.filter(f => f.category === category);
        return (
          <Card
            key={category}
            style={{
              marginBottom: 16,
              borderRadius: 10,
              border: '1px solid #e2e8f0',
              overflow: 'hidden'
            }}
            bodyStyle={{ padding: 0 }}
          >
            <div style={{
              padding: '14px 20px 10px',
              background: `${CATEGORY_COLORS[category]}08`,
              borderBottom: `1px solid ${CATEGORY_COLORS[category]}20`,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}>
              <div style={{
                width: 28, height: 28,
                borderRadius: 6,
                background: `${CATEGORY_COLORS[category]}20`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: CATEGORY_COLORS[category],
                fontSize: 14,
              }}>
                {ALL_FEATURES.find(f => f.category === category)?.icon}
              </div>
              <Text strong style={{ fontSize: 13, color: '#1a202c' }}>{category}</Text>
              <Tag color={CATEGORY_COLORS[category]} style={{ marginLeft: 4, borderRadius: 4, fontSize: 11 }}>
                {rows.length} feature{rows.length !== 1 ? 's' : ''}
              </Tag>
            </div>
            <Table
              dataSource={rows}
              columns={columns}
              pagination={false}
              showHeader={category === categories[0]}
              rowKey="key"
              size="middle"
              style={{ borderRadius: 0 }}
              rowClassName={(_, idx) => idx % 2 === 0 ? '' : 'table-row-alt'}
            />
          </Card>
        );
      })}

      <div style={{ padding: '12px 0', textAlign: 'right' }}>
        <Space>
          {isDirty && (
            <Text type="warning" style={{ fontSize: 13 }}>
              ⚠ You have unsaved changes
            </Text>
          )}
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            disabled={!isDirty}
            size="large"
            style={{ borderRadius: 8 }}
          >
            Save Changes
          </Button>
        </Space>
      </div>

      {isSuperAdmin && (
        <Alert
          type="info"
          showIcon
          icon={<CrownOutlined style={{ color: '#722ed1' }} />}
          style={{ marginTop: 24, borderRadius: 10 }}
          message="Per-condo capability activation (card processing, PAD debit, bulk upload) lives in Feature Toggles. This page only controls per-role sidebar visibility."
        />
      )}
    </div>
  );
};
